import { useState, useEffect } from "react";
import { Calendar, MapPin, Loader, ChevronDown, ChevronUp } from "lucide-react";
import API from "../api";

const MONTH_NAMES = ["","Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const TAG_COLORS = {
  spring: "bg-green-400/10 text-green-400",
  harvest: "bg-yellow-400/10 text-yellow-400",
  spiritual: "bg-purple-400/10 text-purple-400",
  music: "bg-blue-400/10 text-blue-400",
  rare: "bg-red-400/10 text-red-400",
  monsoon: "bg-cyan-400/10 text-cyan-400",
  winter: "bg-slate-400/10 text-slate-300",
  food: "bg-orange-400/10 text-orange-400",
};

function FestivalCard({ f }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ background: 'white', border: '1px solid rgba(74,42,10,0.08)', borderRadius: '16px', padding: '16px 20px', overflow: 'hidden' }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
            <span style={{ fontSize: '11px', fontWeight: 600, background: '#faeeda', color: '#633806', borderRadius: '999px', padding: '4px 12px' }}>
              {f.month_name}
            </span>
            <span style={{ fontSize: '12px', color: '#6b7280' }}>{f.region}</span>
          </div>
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#3a1a00', margin: '0 0 6px' }}>{f.name}</h3>
          <p style={{ color: '#6b7280', fontSize: '14px', margin: '0 0 8px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{f.description}</p>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {(f.tags || []).slice(0, 4).map(t => (
              <span key={t} style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '999px', textTransform: 'capitalize', background: '#faeeda', color: '#633806' }}>
                {t}
              </span>
            ))}
          </div>
        </div>
        <div style={{ flexShrink: 0, color: '#9ca3af', marginTop: '4px' }}>
          {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </div>
      </button>

      {open && (
        <div style={{ paddingTop: '16px', marginTop: '16px', borderTop: '1px solid rgba(74,42,10,0.08)', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div>
            <p style={{ fontSize: '11px', fontWeight: 600, color: '#633806', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px', margin: '0 0 4px' }}>Why it's celebrated</p>
            <p style={{ color: '#3a1a00', fontSize: '14px', margin: 0 }}>{f.why_celebrated}</p>
          </div>
          <div>
            <p style={{ fontSize: '11px', fontWeight: 600, color: '#633806', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px', margin: '0 0 4px' }}>What to see</p>
            <p style={{ color: '#3a1a00', fontSize: '14px', margin: 0 }}>{f.what_to_see}</p>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#6b7280' }}>
              <MapPin size={14} style={{ color: '#633806' }} /> {f.best_place}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#6b7280' }}>
              <Calendar size={14} style={{ color: '#633806' }} /> {f.upcoming_date || f.duration}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Festivals() {
  const [festivals, setFestivals]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [monthFilter, setMonth]     = useState(0);
  const [showAdd, setShowAdd]       = useState(false);
  const [adding, setAdding]         = useState(false);
  const [addForm, setAddForm]       = useState({
    name: "", month_name: "", region: "",
    description: "", why_celebrated: "", best_place: ""
  });

  const loadFestivals = (month) => {
    setLoading(true);
    const params = month ? { month } : {};
    API.get("/festivals", { params })
      .then(r => setFestivals(r.data.festivals || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadFestivals(monthFilter); }, [monthFilter]);

  const submitFestival = async () => {
    if (!addForm.name.trim()) return;
    setAdding(true);
    try {
      await API.post("/festivals", addForm);
      setShowAdd(false);
      setAddForm({ name:"", month_name:"", region:"", description:"", why_celebrated:"", best_place:"" });
      loadFestivals(monthFilter);
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to add festival");
    } finally { setAdding(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#fdf5ec' }}>
      {/* Hero */}
      <div style={{ background: '#4a2a0a', padding: '48px 20px 40px' }}>
        <div style={{ maxWidth: '480px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px' }}>
            <div>
              <h1 style={{ color: 'white', fontSize: '28px', fontWeight: 700, margin: '0 0 8px' }}>Festivals &amp; Events</h1>
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', margin: 0 }}>What locals celebrate and why — the real stories behind the traditions</p>
            </div>
            <button
              onClick={() => setShowAdd(s => !s)}
              style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 600, fontSize: '13px', padding: '8px 16px', borderRadius: '999px', border: '1px solid rgba(255,255,255,0.3)', cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              + Add
            </button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ maxWidth: '640px', margin: '0 auto', padding: '24px 16px 80px' }}>

        {/* add form */}
        {showAdd && (
          <div style={{ background: 'white', border: '1px solid rgba(74,42,10,0.15)', borderRadius: '16px', padding: '20px', marginBottom: '24px' }}>
            <h3 style={{ fontWeight: 600, color: '#4a2a0a', marginBottom: '16px', margin: '0 0 16px' }}>Add a Festival</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[
                { key: "name",            placeholder: "Festival name *" },
                { key: "month_name",      placeholder: "Month (e.g. March)" },
                { key: "region",          placeholder: "Region (e.g. Kumaon & Garhwal)" },
                { key: "description",     placeholder: "Description" },
                { key: "why_celebrated",  placeholder: "Why is it celebrated?" },
                { key: "best_place",      placeholder: "Best place to witness it" },
              ].map(field => (
                <input key={field.key}
                  placeholder={field.placeholder}
                  value={addForm[field.key]}
                  onChange={e => setAddForm(p => ({ ...p, [field.key]: e.target.value }))}
                  style={{ width: '100%', background: '#fdf5ec', border: '1px solid rgba(74,42,10,0.12)', borderRadius: '8px', padding: '10px 14px', fontSize: '14px', color: '#3a1a00', outline: 'none', boxSizing: 'border-box' }}
                />
              ))}
              <div style={{ display: 'flex', gap: '12px' }}>
                <button onClick={() => setShowAdd(false)}
                  style={{ flex: 1, border: '1px solid rgba(74,42,10,0.15)', color: '#3a1a00', padding: '10px', borderRadius: '999px', fontSize: '13px', background: 'transparent', cursor: 'pointer' }}>
                  Cancel
                </button>
                <button onClick={submitFestival} disabled={adding || !addForm.name.trim()}
                  style={{ flex: 1, background: '#4a2a0a', color: 'white', fontWeight: 600, padding: '10px', borderRadius: '999px', fontSize: '13px', border: 'none', cursor: 'pointer', opacity: adding || !addForm.name.trim() ? 0.4 : 1 }}>
                  {adding ? "Saving..." : "Submit"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* month filter */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
          <button onClick={() => setMonth(0)}
            style={{
              padding: '8px 16px',
              borderRadius: '999px',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.2s',
              background: monthFilter === 0 ? '#4a2a0a' : 'white',
              color: monthFilter === 0 ? 'white' : '#888',
              border: monthFilter === 0 ? 'none' : '1px solid rgba(0,0,0,0.12)',
            }}>
            All
          </button>
          {MONTH_NAMES.slice(1).map((m, i) => (
            <button key={i+1} onClick={() => setMonth(i + 1)}
              style={{
                padding: '8px 16px',
                borderRadius: '999px',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.2s',
                background: monthFilter === i+1 ? '#4a2a0a' : 'white',
                color: monthFilter === i+1 ? 'white' : '#888',
                border: monthFilter === i+1 ? 'none' : '1px solid rgba(0,0,0,0.12)',
              }}>
              {m}
            </button>
          ))}
        </div>

        {/* list */}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '64px 0' }}>
            <Loader size={24} style={{ color: '#4a2a0a', animation: 'spin 1s linear infinite' }} />
          </div>
        ) : festivals.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#6b7280', padding: '64px 0' }}>No festivals found. Be the first to add one!</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {festivals.map((f, i) => <FestivalCard key={f.id || i} f={f} />)}
          </div>
        )}
      </div>
    </div>
  );
}
